import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../redux/store";
import {
  initPostHog,
  identifyUser,
  trackPageView,
  resetUser,
} from "../lib/posthog";
import { getCurrentUserDetailsFromLocalStorage } from "../api/authApi";
import { logger } from "../utils/consoleLogger";

interface PostHogProviderProps {
  children: React.ReactNode;
}

const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.user.user);
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);

  useEffect(() => {
    initPostHog();
    logger.info("PostHog initialized");
  }, []);

  useEffect(() => {
    const authData = getCurrentUserDetailsFromLocalStorage();

    if (isAuthenticated && user && authData) {
      const userProperties = {
        email: user.email || authData.email,
        name: user.name,
        slug: user.slug || authData.slug,
        company_name: user.company_name,
        is_trial: user.is_trial,
        role: user.role,
        phone: user.phone,
        designation: user.designation,
      };

      // Remove undefined values
      Object.keys(userProperties).forEach((key) => {
        if (userProperties[key] === undefined) {
          delete userProperties[key];
        }
      });

      identifyUser(user.email || authData.email, userProperties);
    } else if (!isAuthenticated) {
      // Reset user on logout
      resetUser();
    }
  }, [isAuthenticated, user]);

  // Track page views on route change
  useEffect(() => {
    const pathname = location.pathname;
    const searchParams = location.search;
    const hash = location.hash;

    // Extract route information
    const pathParts = pathname.split("/").filter(Boolean);
    const slug = pathParts[0];

    let pageName = "Unknown";
    let pageCategory = "Other";

    if (
      pathname === "/" ||
      pathname === `/${slug}` ||
      pathname === `/${slug}/`
    ) {
      pageName = "Home";
      pageCategory = "Dashboard";
    } else if (
      pathname.includes("/role") &&
      !pathname.includes("/roles/create")
    ) {
      if (pathname.includes("/candidates")) {
        pageName = "Candidates View";
        pageCategory = "Candidates";
      } else if (pathname.includes("/invite-candidates")) {
        pageName = "Invite Candidates";
        pageCategory = "Candidates";
      } else if (pathname.includes("/calendly")) {
        pageName = "Calendly Integration";
        pageCategory = "Integration";
      } else if (pathname.includes("/edit")) {
        pageName = "Edit Role";
        pageCategory = "Role Management";
      } else if (pathname.endsWith("/active") || pathname.includes("/active")) {
        pageName = "Active Roles List";
        pageCategory = "Role Management";
      } else if (pathname.endsWith("/draft") || pathname.includes("/draft")) {
        pageName = "Draft Roles List";
        pageCategory = "Role Management";
      } else {
        pageName = "Role Details";
        pageCategory = "Role Management";
      }
    } else if (pathname.includes("/roles/create")) {
      pageName = "Create Role";
      pageCategory = "Role Management";
    } else if (pathname.includes("/candidate/")) {
      pageName = "Candidate Profile";
      pageCategory = "Candidates";
    } else if (pathname.includes("/report/")) {
      pageName = "Candidate Report";
      pageCategory = "Reports";
    } else if (pathname.includes("/pdf/")) {
      pageName = "PDF Report";
      pageCategory = "Reports";
    } else if (pathname.includes("/verify")) {
      pageName = "Verify OTP";
      pageCategory = "Authentication";
    } else if (pathname.includes("/chat")) {
      pageName = "Chat";
      pageCategory = "Authentication";
    } else if (pathname.includes("/join")) {
      pageName = "Join Organization";
      pageCategory = "Authentication";
    } else if (pathname.includes("/request")) {
      pageName = "Request Access";
      pageCategory = "Authentication";
    } else if (pathname.includes("/create")) {
      pageName = "Create Organization";
      pageCategory = "Authentication";
    }

    // Track page view with metadata
    trackPageView(pageName, {
      path: pathname,
      search: searchParams,
      hash: hash,
      page_category: pageCategory,
      slug: slug,
      full_url: window.location.href,
    });
  }, [location]);

  return <>{children}</>;
};

export default PostHogProvider;
